import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  SimpleGrid,
  Center,
  Flex,
  Box,
  Button,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { Spinner } from '@/components/atoms/Spinner'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { EXPLORERS } from '@/constants/constants'
import { MINES_GAME_DETAILS_QUERY } from '@/api/query'
import { Game } from 'types/types'
import { ShareResultContent } from '../ShareResultModal/ShareResultModal'
import { BackButton } from '@/components/atoms/BackButton'
import { transformMinesGame } from '@/helpers/transformMinesGame'
import { MinesGamePlayground } from '@/components/organisms/MinesGamePlayground/MinesGamePlayground'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import Link from 'next/link'
import ShareIcon from '@/public/assets/icons/share.svg'
import { UserGameResult } from '../UserGameResult/UserGameResult'
import { TokenMoney } from '@ninjablaze/sdk-ts'

enum Steps {
  DETAILS,
  SHARING,
}

export default ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(Steps.DETAILS)

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          <Flex gap={2} alignItems={'center'}>
            {step === Steps.SHARING && (
              <BackButton callback={() => setStep(Steps.DETAILS)} styles={{ h: '30px', w: '30px', p: 1.5 }} />
            )}
            {t('common.game-details', { gameId })}
          </Flex>
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          {step === Steps.DETAILS && <MinesGameDetails switchTab={() => setStep(Steps.SHARING)} gameId={gameId} />}
          {step === Steps.SHARING && <ShareResultContent game={Game.MINES} gameId={gameId} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const MinesGameDetails = ({ gameId, switchTab }: { gameId: string; switchTab: () => void }) => {
  const { t } = useTranslation()
  const { data, refetch, error } = useQuery(MINES_GAME_DETAILS_QUERY, {
    variables: { id: gameId },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (error) {
    return <Text color={'ninja.200'}>{t('common.error-occurred')}</Text>
  }

  const game = transformMinesGame(data?.minesGame)

  if (!game) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  return (
    <>
      <UserGameResult
        address={game.player}
        multiplier={(parseInt(game.win) / parseInt(game.bet)).toFixed(2)}
        payout={{ amount: game.win, denom: game.denom || 'inj' }}
        result={game.result}
      />
      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }}>
        <StatsRow
          name={t('common.game-status')}
          value={
            <Text color={'brand.green'} fontWeight={700}>
              {t('common.finished')}
            </Text>
          }
        />
        <StatsRow
          name={t('common.bet')}
          value={
            <FiatOrCryptoAmount
              iconSize={'18px'}
              denom={game.denom || 'inj'}
              value={new TokenMoney(game.bet, game.denom || 'inj')}
            />
          }
        />
        <StatsRow
          name={t('common.tx-hash')}
          value={<CopiableLink link={EXPLORERS.INJECTIVE_TRANSACTIONS + game.txHash} value={game.txHash} />}
        />
        <StatsRow name={t('common.date')} value={new Date(game.date).toLocaleString()} />
      </SimpleGrid>
      <Box mb={5}>
        <MinesGamePlayground mines={game.mines} userChoices={game.userChoices} isVertical={true} />
      </Box>
      <SimpleGrid columns={2} gap={3} mt={1}>
        <Button variant={'orange'} rounded={'xl'} gap={2.5} onClick={switchTab}>
          {t('common.share')} <ShareIcon />
        </Button>
        <Link href={'/mines/history/' + game.id}>
          <Button w={'full'} rounded={'xl'}>
            {t('common.more-details')}
          </Button>
        </Link>
      </SimpleGrid>
    </>
  )
}
